import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Dialog,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from "@material-ui/core"
import { Alert, Color } from "@material-ui/lab"
import { useQueryClient } from "@tanstack/react-query"
import { FC, SyntheticEvent, useState } from "react"
import {
  useGetClientDataQuery,
  useUpdateInviteCodeStatusMutation,
} from "../../generated/graphql"
import { useAIServices } from "../../hooks/useAIServices"
import { useDisconnection } from "../../hooks/useDisconnection"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useNavigation } from "../../hooks/useNavigation"
import { useUser } from "../../hooks/useUser"
import { useApi } from "../../utils/API"
import { InviteCodeStatus, Query } from "../../utils/constants"
import { colors, theme } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"
import AddEditClientDialog from "../organisms/AddEditClientDialog"

const useStyles = makeStyles({
  moreButton: {
    fill: colors.neutral.denim,
    stroke: colors.neutral.denim,
    "&:hover": {
      fill: colors.neutral.asphalt,
      stroke: colors.neutral.asphalt,
    },
    "&:active": {
      fill: colors.primary.periwinkle,
      stroke: colors.primary.periwinkle,
    },
    "&:focus": {
      fill: colors.primary.shadow,
      stroke: colors.primary.shadow,
    },
    fontSize: 18,
  },
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  },
  closeButton: {
    margin: 0,
    padding: 0,
    border: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  dialogSubtitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
  confirmButton: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  cancelContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  menuPaper: {
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: colors.neutral.white,
    boxShadow:
      "0px 0px 4px rgba(54, 53, 69, 0.1), 0px 3px 12px rgba(54, 53, 69, 0.05)",
  },
  iconButton: {
    "&:hover": {
      backgroundColor: "transparent",
      boxShadow: "none",
      outline: "none",
    },
    "&:focus": {
      outline: "none",
    },
  },
})

interface Props {
  clientId: string
  code: string
  status: InviteCodeStatus
  firstName: string
  lastName: string
  displayName: string
  partner?: boolean
  clientDetail?: boolean
}

const ClientMoreButton: FC<Props> = ({
  clientId,
  code,
  status,
  firstName,
  lastName,
  displayName,
  partner,
  clientDetail,
}) => {
  const classes = useStyles()
  const { user } = useUser()
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false)
  const [openReaddDialog, setOpenReaddDialog] = useState<boolean>(false)
  const [openEditClientDialog, setOpenEditClientDialog] =
    useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const queryClient = useQueryClient()
  const graphqlClient = useGraphQLClient()
  const API = useApi()
  const isDisconnected =
    status === InviteCodeStatus.CLIENT_DISCONNECTED ||
    status === InviteCodeStatus.PRACTITIONER_DISCONNECTED ||
    status === InviteCodeStatus.DECLINED ||
    status === InviteCodeStatus.PENDING

  const { toDashboard } = useNavigation()

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })

  const onSuccess = (data: any) => {
    if (
      data?.update_invite_codes.returning.length === 0 ||
      data?.delete_invite_codes.returning.length === 0
    ) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Oops! Something went wrong. Please try again later.",
      })
      return
    }
    queryClient.invalidateQueries({
      queryKey: [Query.GET_USERS_BY_PRACTITIONER],
    })
  }

  const clientData = useGetClientDataQuery(graphqlClient, {
    clientId: clientId,
  })
  const clientDemographicData =
    clientData.data?.users_by_pk?.client_demographics[0]
  const { mutate: updateInviteCodeStatus } = useUpdateInviteCodeStatusMutation(
    graphqlClient,
    {
      onSuccess,
    }
  )

  const [loading, setLoading] = useState<boolean>(false)
  const { isAIServicesMember } = useAIServices()

  const { disconnectPractitioner } = useDisconnection()

  const handleDisconnect = async () => {
    if (!clientId || !user?.id) return
    try {
      await disconnectPractitioner(clientId, user.id)
      // Force refresh of client data
      await queryClient.invalidateQueries({
        queryKey: ["GetUsersByPractitioner"],
      })
      await queryClient.refetchQueries({ queryKey: ["GetUsersByPractitioner"] })
      if (clientDetail && !partner) {
        toDashboard()
      }
    } catch (error) {}
  }

  const handleCloseSnackbar = (
    event: SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        className={classes.iconButton}
        classes={{ label: classes.moreButton }}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        keepMounted={true}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{
          paper: classes.menuPaper,
        }}
      >
        {status === InviteCodeStatus.CONNECTED ? (
          <>
            {isAIServicesMember && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null)
                  setOpenEditClientDialog(true)
                }}
              >
                Edit Client Profile
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
                setOpenRemoveDialog(true)
              }}
              disabled={isDisconnected}
              style={{ fontStyle: isDisconnected ? "italic" : "normal" }}
            >
              {isDisconnected ? "Account Disconnected" : "Disconnect Client"}
            </MenuItem>
          </>
        ) : (
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              setOpenReaddDialog(true)
            }}
            disabled={true}
          >
            Account Not Connected
          </MenuItem>
        )}
      </Menu>

      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={openRemoveDialog}
        onClose={() => setOpenRemoveDialog(false)}
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogContent}>
          <div className={classes.dialogCloseContainer}>
            <button
              className={classes.closeButton}
              onClick={() => setOpenRemoveDialog(false)}
            >
              <CloseIcon />
            </button>
          </div>
          <Typography
            variant="h2"
            component="h1"
            className={classes.dialogTitle}
          >
            Disconnect Client
          </Typography>
          <Typography variant="body1" className={classes.dialogSubtitle}>
            {`Are you sure you want to disconnect with ${firstName} ${lastName}? Data uploaded to the platform will remain available, only if it was uploaded prior to disconnection. While any new data uploaded after disconnection will not be accessible, unless you reconnect with this client in the future. All content scheduled by you will cease.`}
          </Typography>
          <PrimaryButton
            onClick={() => {
              setLoading(true)
              handleDisconnect()
              API.scheduleDisconnectPractitionerNotification(code)
              setLoading(false)
              setOpenRemoveDialog(false)
            }}
            className={classes.confirmButton}
            type="submit"
            disabled={loading}
          >
            Confirm
          </PrimaryButton>
          <div className={classes.cancelContainer}>
            <GhostButton onClick={() => setOpenRemoveDialog(false)}>
              Cancel
            </GhostButton>
          </div>
        </div>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={openReaddDialog}
        onClose={() => setOpenReaddDialog(false)}
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogContent}>
          <div className={classes.dialogCloseContainer}>
            <button
              className={classes.closeButton}
              onClick={() => setOpenReaddDialog(false)}
            >
              <CloseIcon />
            </button>
          </div>
          <Typography
            variant="h2"
            component="h1"
            className={classes.dialogTitle}
          >
            Re-Add Client
          </Typography>
          <Typography variant="body1" className={classes.dialogSubtitle}>
            {`Are you sure you want to re-add ${firstName} ${lastName}?`}
          </Typography>
          <PrimaryButton
            onClick={() => {
              setLoading(true)
              updateInviteCodeStatus({
                code,
                status: InviteCodeStatus.PENDING,
              })
              API.scheduleConnectPractitionerNotification(code.toUpperCase())
              setLoading(false)
              setOpenReaddDialog(false)
            }}
            className={classes.confirmButton}
            type="submit"
            disabled={loading}
          >
            Confirm
          </PrimaryButton>
          <div className={classes.cancelContainer}>
            <GhostButton onClick={() => setOpenReaddDialog(false)}>
              Cancel
            </GhostButton>
          </div>
        </div>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <AddEditClientDialog
        open={openEditClientDialog}
        onClose={() => setOpenEditClientDialog(false)}
        demographicsDataId={clientDemographicData?.id ?? undefined}
        userId={clientId}
        firstName={firstName}
        lastName={lastName}
        email={displayName}
        birthYear={clientDemographicData?.birth_year ?? undefined}
        gender={clientDemographicData?.gender ?? undefined}
        racialEthnic={clientDemographicData?.racial_ethnic ?? undefined}
        presentingProblem={clientDemographicData?.description ?? ""}
      />
    </>
  )
}

export default ClientMoreButton
